import { Checkbox, Input, Select } from '@components'
import {
  ExpenseClaimTravelDay,
  ExpenseClaimTrip,
  PerDiemDestination,
  expenseClaimFieldError,
  patchExpenseClaimTravelDay
} from '@query'
import { formatEurosWithOpts } from '@utils/formatCurrency'
import { useMutation } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Help } from './shared'

type PerDiemSectionProps = {
  slug: string
  claimId: number
  trip: ExpenseClaimTrip | undefined
  destinations: PerDiemDestination[]
  canEdit: boolean
  autoPerDiem: boolean
  onAutoChange: (next: boolean) => void
  onChanged: () => void
  onError: (message: string) => void
}

const centsToField = (cents: number | null) =>
  cents === null ? '' : (cents / 100).toFixed(2).replace('.', ',')

const fieldToCents = (raw: string): number | null | undefined => {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const normalized = trimmed.replace(/\s/g, '').replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return undefined
  return Math.round(Number(normalized) * 100)
}

import { formatDateTime } from '@utils'

export const PerDiemSection: React.FC<PerDiemSectionProps> = ({
  slug,
  claimId,
  trip,
  destinations,
  canEdit,
  autoPerDiem,
  onAutoChange,
  onChanged,
  onError
}) => {
  const [t] = useTranslation()
  const [manualDrafts, setManualDrafts] = useState<Record<number, string>>({})
  const [manualErrors, setManualErrors] = useState<Record<number, string>>({})
  const automaticAllowed = !trip || trip.per_diem_automatic_allowed
  const days = trip?.travel_days ?? []

  useEffect(() => {
    const next: Record<number, string> = {}
    for (const day of trip?.travel_days ?? []) {
      next[day.id] = centsToField(day.manual_amount)
    }
    setManualDrafts(next)
    setManualErrors({})
  }, [trip])

  const patchDay = useMutation({
    mutationFn: (input: {
      day: ExpenseClaimTravelDay
      body: {
        free_meals?: number
        manual_amount?: number | null
        meal_allowance?: boolean
        destination_override?: string | null
      }
    }) => patchExpenseClaimTravelDay(slug, claimId, trip?.id ?? 0, input.day.id, input.body),
    onSuccess: () => onChanged(),
    onError: (saveError, input) => {
      const message =
        expenseClaimFieldError(saveError, 'manual_amount') ??
        expenseClaimFieldError(saveError, 'meal_allowance') ??
        expenseClaimFieldError(saveError, 'free_meals') ??
        expenseClaimFieldError(saveError, 'destination_override') ??
        t('travelAndExpenses.loadError')
      if (expenseClaimFieldError(saveError, 'manual_amount')) {
        setManualErrors(current => ({ ...current, [input.day.id]: message }))
        return
      }
      onError(message)
    }
  })

  const saveManual = (day: ExpenseClaimTravelDay) => {
    const cents = fieldToCents(manualDrafts[day.id] ?? '')
    if (cents === undefined) {
      setManualErrors(current => ({
        ...current,
        [day.id]: t('travelAndExpenses.trip.manualInvalid')
      }))
      return
    }
    if (cents === day.manual_amount) return
    setManualErrors(current => ({ ...current, [day.id]: '' }))
    patchDay.mutate({ day, body: { manual_amount: cents } })
  }

  return (
    <>
      <Checkbox
        name="auto_per_diem"
        label={t('travelAndExpenses.trip.perDiemAuto')}
        checked={automaticAllowed && autoPerDiem}
        disabled={!canEdit || !automaticAllowed}
        onChange={event => onAutoChange(event.target.checked)}
      />
      <Help>{t('travelAndExpenses.trip.perDiemCounted')}</Help>
      {trip && !trip.per_diem_automatic_allowed && (
        <Help>{t('travelAndExpenses.trip.perDiemTooShort')}</Help>
      )}
      {!trip && <Help>{t('travelAndExpenses.trip.saveFirst')}</Help>}
      {days.map(day => {
        const perDiemPaid = day.allowance_type !== 'NONE'
        return (
          <Day key={day.id}>
            <DayHead>
              <div>
                <DayTitle>
                  {t('travelAndExpenses.trip.day', { index: day.day_index + 1 })}
                  {' · '}
                  {day.destination_label}
                </DayTitle>
                <Help>
                  {formatDateTime(day.starts_at)} – {formatDateTime(day.ends_at)}
                </Help>
              </div>
              <DayAmount>
                <span>{t(`travelAndExpenses.trip.allowance.${day.allowance_type}`)}</span>
                <strong>{formatEurosWithOpts(day.amount)}</strong>
              </DayAmount>
            </DayHead>
            <Select
              name={`destination_override_${day.id}`}
              label={t('travelAndExpenses.trip.regionOnDay')}
              value={day.destination_override ?? ''}
              disabled={!canEdit || patchDay.isPending}
              onChange={event =>
                patchDay.mutate({
                  day,
                  body: { destination_override: event.target.value || null }
                })
              }
            >
              <option value="">{t('travelAndExpenses.trip.regionAutomatic')}</option>
              {destinations.map(row => (
                <option key={row.key} value={row.key}>
                  {row.label}
                </option>
              ))}
            </Select>
            <Select
              name={`free_meals_${day.id}`}
              label={t('travelAndExpenses.trip.freeMeals')}
              value={String(day.free_meals)}
              disabled={!canEdit || patchDay.isPending}
              onChange={event =>
                patchDay.mutate({ day, body: { free_meals: Number(event.target.value) } })
              }
            >
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
            </Select>
            <Checkbox
              name={`meal_allowance_${day.id}`}
              label={t('travelAndExpenses.trip.mealAllowance')}
              info={perDiemPaid ? t('travelAndExpenses.trip.perDiemPaid') : undefined}
              checked={day.meal_allowance}
              disabled={!canEdit || perDiemPaid || patchDay.isPending}
              onChange={event =>
                patchDay.mutate({ day, body: { meal_allowance: event.target.checked } })
              }
            />
            <Input
              name={`manual_amount_${day.id}`}
              label={t('travelAndExpenses.trip.enterManually')}
              info={t('travelAndExpenses.trip.atMostTaxFree', {
                amount: formatEurosWithOpts(day.tax_free_amount)
              })}
              value={manualDrafts[day.id] ?? ''}
              disabled={!canEdit || patchDay.isPending}
              errors={manualErrors[day.id] ? { message: manualErrors[day.id] } : undefined}
              onChange={event =>
                setManualDrafts(current => ({ ...current, [day.id]: event.target.value }))
              }
              onBlur={() => saveManual(day)}
            />
          </Day>
        )
      })}
    </>
  )
}

const Day = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  padding-top: ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const DayHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const DayTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const DayAmount = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.metalGray};

  strong {
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`
